import { Dialog } from "@headlessui/react";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useState } from "react";
import { ILead } from "@/types/lead.type";
import InputFormik from "@/common/components/form/InputFormik";
import { Form, Formik } from "formik";
import { maskCPF } from "@/common/helpers/mask.helpers";
import {
    getRenterByCpf,
    updateRenterProps,
} from "@/common/services/renters.api";

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (collectorData: {
        cpf: string;
        pixType: string;
        pixKey: string;
    }) => void;
    openConfirmEdit: (leadData: ILead) => void;
    roles: string[] | undefined;
}

export default function ModalCreateCollector({
    open,
    onClose,
    onSave,
}: Props) {
    const { notifySuccess, notifyError } = useToastContext();
    const [, setFormErrors] = useState<{ cpf?: string; pixKey?: string }>({});

    const handleSave = async (collectorData: {
        cpf: string;
        pixType: string;
        pixKey: string;
    }) => {
        const { cpf, pixKey, pixType } = collectorData;
        const errors: { cpf?: string; pixKey?: string } = {};

        if (!cpf) errors.cpf = "CPF é obrigatório.";
        if (!pixKey) errors.pixKey = "A chave Pix é obrigatória.";

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            notifyError("Preencha todos os campos obrigatórios.");
            return;
        }

        setFormErrors({});

        try {
            const { data: renter, ok } = await getRenterByCpf(cpf);
            if (!ok || !renter?._id) {
                notifyError("CPF não encontrado.");
                setFormErrors({ cpf: "CPF não encontrado." });
                return;
            }

            const updatePayload = {
                dataPix: { key: pixKey, keytype: pixType },
                collector: true,
            };

            const updateResult = await updateRenterProps(renter._id, updatePayload);

            if (updateResult.ok) {
                notifySuccess("Recolhedor atualizado com sucesso!");
                await onSave(collectorData);
                onClose();
            } else {
                notifyError("Falha ao atualizar o recolhedor.");
            }
        } catch (error) {
            notifyError("Erro ao atualizar. Verifique o console.");
            console.error(error);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg space-y-4">
                    <Dialog.Title className="text-xl font-semibold text-black">
                        Adicionar Recolhedor
                    </Dialog.Title>

                    <Formik
                        initialValues={{ cpf: "", pixType: "", pixKey: "" }}
                        onSubmit={(values) => handleSave(values)}
                    >
                        {({ values, handleChange, errors }) => (
                            <Form className="space-y-4 mt-4">
                                <div>
                                    <label className="text-sm font-medium">CPF</label>
                                    <InputFormik
                                        type="text"
                                        name="cpf"
                                        value={values.cpf}
                                        onChange={handleChange}
                                        placeholder="Digite o CPF"
                                        mask={maskCPF}
                                    />
                                    {errors.cpf && (
                                        <p className="text-sm text-red-500 mt-1">{errors.cpf}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Tipo da Chave Pix</label>
                                    <select
                                        name="pixType"
                                        value={values.pixType}
                                        onChange={handleChange}
                                        className="w-full border rounded px-3 py-2"
                                    >
                                        <option value="">Selecione</option>
                                        <option value="Telefone">Telefone</option>
                                        <option value="CPF">CPF</option>
                                        <option value="Email">Email</option>
                                        <option value="Aleatória">Chave Aleatória</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-sm font-medium">Chave Pix</label>
                                    <input
                                        type="text"
                                        name="pixKey"
                                        value={values.pixKey}
                                        onChange={handleChange}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="Digite a chave Pix"
                                    />
                                    {errors.pixKey && (
                                        <p className="text-sm text-red-500 mt-1">{errors.pixKey}</p>
                                    )}
                                </div>

                                <div className="flex justify-end gap-3 pt-4">
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-gray-200 rounded-md text-black"
                                        onClick={onClose}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-locavibe text-white rounded-md"
                                    >
                                        Salvar
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}